import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with auth header
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests (using streamToken for consistency with other services)
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('streamToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('streamToken');
      localStorage.removeItem('streamUser');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class SubscriptionService {
  // Get all subscription plans (public)
  async getPlans() {
    try {
      const response = await axios.get(`${API_URL}/subscription/plans`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get current user's subscription
  async getMySubscription() {
    try {
      const response = await axiosInstance.get('/subscription/my-subscription');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get payment history
  async getPayments(limit = 50, offset = 0) {
    try {
      const response = await axiosInstance.get(`/subscription/payments?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Check subscription status
  async checkStatus() {
    try {
      const response = await axiosInstance.get('/subscription/check-status');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get usage statistics
  async getUsage() {
    try {
      const response = await axiosInstance.get('/subscription/usage');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Create Stripe checkout session
  async createCheckoutSession(planId, billingCycle) {
    try {
      const response = await axiosInstance.post('/stripe/create-checkout-session', {
        plan_id: planId,
        billing_cycle: billingCycle
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get checkout session status
  async getCheckoutSession(sessionId) {
    try {
      const response = await axiosInstance.get(`/stripe/checkout-session/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Verify checkout session and update subscription (called from success page)
  async verifyCheckoutSession(sessionId) {
    try {
      const response = await axiosInstance.get(`/stripe/checkout-session/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Create customer portal session
  async createPortalSession() {
    try {
      const response = await axiosInstance.post('/stripe/create-portal-session');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Cancel subscription
  async cancelSubscription(atPeriodEnd = true) {
    try {
      const response = await axiosInstance.post('/stripe/cancel-subscription', {
        at_period_end: atPeriodEnd
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Reactivate subscription
  async reactivateSubscription() {
    try {
      const response = await axiosInstance.post('/stripe/reactivate-subscription');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Get all plans
  async getAllPlans() {
    try {
      const response = await axiosInstance.get('/subscription/admin/plans');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Create plan
  async createPlan(planData) {
    try {
      const response = await axiosInstance.post('/subscription/admin/plans', planData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Update plan
  async updatePlan(planId, planData) {
    try {
      const response = await axiosInstance.put(`/subscription/admin/plans/${planId}`, planData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Delete plan
  async deletePlan(planId) {
    try {
      const response = await axiosInstance.delete(`/subscription/admin/plans/${planId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Get all subscriptions (optional { status, q })
  async getAllSubscriptions(params = {}) {
    try {
      const response = await axiosInstance.get('/subscription/admin/subscriptions', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getSubscriptionDetail(id) {
    try {
      const r = await axiosInstance.get(`/subscription/admin/subscriptions/${id}`);
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  async updateSubscription(id, data) {
    try {
      const r = await axiosInstance.put(`/subscription/admin/subscriptions/${id}`, data);
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  async extendSubscription(id, days) {
    try {
      const r = await axiosInstance.post(`/subscription/admin/subscriptions/${id}/extend`, { days });
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  async cancelSubscriptionAdmin(id, atPeriodEnd = false) {
    try {
      const r = await axiosInstance.post(`/subscription/admin/subscriptions/${id}/cancel`, { at_period_end: atPeriodEnd });
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  async refundSubscription(id, { amount, reason, note, keep_active } = {}) {
    try {
      const r = await axiosInstance.post(`/subscription/admin/subscriptions/${id}/refund`, { amount, reason, note, keep_active });
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  async getDisputes() {
    try {
      const r = await axiosInstance.get('/subscription/admin/disputes');
      return r.data;
    } catch (e) { throw e.response?.data || e; }
  }

  // Admin: Get all payments
  async getAllPayments(limit = 100, offset = 0) {
    try {
      const response = await axiosInstance.get(`/subscription/admin/payments?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Search payments
  async searchPayments({ invoiceId, transactionId, status, userId, limit = 100, offset = 0 }) {
    try {
      let url = `/subscription/admin/payments?limit=${limit}&offset=${offset}`;
      
      if (invoiceId) url += `&invoice_id=${encodeURIComponent(invoiceId)}`;
      if (transactionId) url += `&transaction_id=${encodeURIComponent(transactionId)}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      if (userId) url += `&user_id=${encodeURIComponent(userId)}`;
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Admin: Get revenue stats
  async getRevenueStats(startDate, endDate) {
    try {
      let url = '/subscription/admin/revenue';
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new SubscriptionService();
